import { createFileRoute, Link } from "@tanstack/react-router";
import { fitCallUrl } from "@/lib/booking";
import { ArrowUpRight } from "lucide-react";
import type { Post } from "@/data/posts";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, blogSchema } from "@/lib/seo";
import { publishedAt, modifiedAt, formatPostDate } from "@/data/postDates";
import { NextStep } from "@/components/site/NextStep";

const newPosts: Post[] = [
  {
    slug: "fractional-coo-cost-australia",
    category: "Pricing",
    read: "5 min read",
    title: "How much does a fractional COO cost in Australia?",
    dek: "Published Australian guides put a fractional COO at $6,000 to $25,000 a month. Ajility publishes its actual prices: composed retainers for businesses under $15M start from $3,900 a month, plus GST.",
    description: "Published Australian guides put a fractional COO at $6,000 to $25,000 a month. Ajility publishes its actual prices: composed retainers for businesses under $15M start from $3,900 a month, plus GST.",
    html: "",
  },
  {
    slug: "the-6m-price-review",
    category: "Pricing",
    read: "4 min read",
    title: "The $6M price review",
    dek: "One annual price review cycle at a 249-store network produced more than $6M in first-year revenue growth. What that says about untested prices in founder-led businesses.",
    description: "One annual price review cycle at a 249-store network produced more than $6M in first-year revenue growth. What that says about untested prices in founder-led businesses.",
    html: "",
  },
  {
    slug: "mining-services-first-90-days",
    category: "Case study",
    read: "4 min read",
    title: "Ninety days into a mining services engagement",
    dek: "What changed in the first 90 days of an Ajility engagement with a mining services business: a one-page plan, a weekly cadence, forecasting, and about $1M of annualised overhead removed.",
    description: "What changed in the first 90 days of an Ajility engagement with a mining services business: a one-page plan, a weekly cadence, forecasting, and about $1M of annualised overhead removed.",
    html: "",
  },
  {
    slug: "pricing-tools-5-billion-business",
    category: "Pricing",
    read: "4 min read",
    title: "The pricing tools a $5 billion business runs on",
    dek: "What building pricing systems inside a $5 billion beverage business teaches about pricing discipline in a $5M business: it is the system and the owner, not the size, that make prices hold.",
    description: "What building pricing systems inside a $5 billion beverage business teaches about pricing discipline in a $5M business: it is the system and the owner, not the size, that make prices hold.",
    html: "",
  },
  {
    slug: "a-plan-nobody-tracks-is-a-document",
    category: "Planning",
    read: "4 min read",
    title: "A plan nobody tracks is a document",
    dek: "The difference between a strategy day and an operating system: a budget behind the plan, a forecast that gets updated, and a monthly rhythm where someone answers for the variance.",
    description: "The difference between a strategy day and an operating system: a budget behind the plan, a forecast that gets updated, and a monthly rhythm where someone answers for the variance.",
    html: "",
  },
  {
    slug: "why-we-publish-our-prices",
    category: "Pricing",
    read: "3 min read",
    title: "Why we publish our prices",
    dek: "Ajility publishes its complete price ladder, from the free Operating Scan to every install and retainer. Why a pricing specialist who hid his prices would be telling you something.",
    description: "Ajility publishes its complete price ladder, from the free Operating Scan to every install and retainer. Why a pricing specialist who hid his prices would be telling you something.",
    html: "",
  },
  {
    slug: "the-six-minute-operating-scan",
    category: "Tools",
    read: "3 min read",
    title: "The six-minute Operating Scan",
    dek: "21 plain statements about how the business runs, answered from Not us to Always. In about six minutes, see a provisional score naming the weakest areas.",
    description: "21 plain statements about how the business runs, answered from Not us to Always. In about six minutes, see a provisional score naming the weakest areas.",
    html: "",
  },
];

const newSlugs = new Set(newPosts.map((p) => p.slug));

export const Route = createFileRoute("/insights/")({
  loader: async () => {
    const { posts } = await import("@/data/posts");
    return { allPosts: [...newPosts, ...posts.filter((post) => !newSlugs.has(post.slug))].map(({ html: _html, ...post }) => post) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "Insights: Operating Notes & Case Studies | Ajility" },
      {
        name: "description",
        content:
          "Field notes and anonymised case studies from 15+ years installing operating systems inside founder-led Australian businesses.",
      },
      { property: "og:title", content: "Insights: Operating Notes & Case Studies | Ajility" },
      {
        property: "og:description",
        content:
          "Field notes and anonymised case studies from 15+ years installing operating systems inside founder-led Australian businesses.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Insights: Operating Notes & Case Studies | Ajility" },
      { name: "twitter:description", content: "Field notes and anonymised case studies from 15+ years installing operating systems inside founder-led Australian businesses." },
      ...ogImage("insights", "Ajility operating insights and case studies"),
      ogUrl("/insights"),
    ],
    links: canonicalLinks("/insights"),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Insights", path: "/insights" }])),
      jsonLd(
        blogSchema(
          (loaderData?.allPosts ?? []).map((p) => ({
            slug: p.slug,
            title: p.title,
            description: p.description,
            datePublished: publishedAt(p.slug),
          })),
        ),
      ),
    ],
  }),
  component: InsightsIndex,
});

function InsightsIndex() {
  const { allPosts } = Route.useLoaderData();
  const [feature, ...rest] = allPosts;
  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative gradient-navy text-frost">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-40 sm:px-8 sm:pt-48">
          <span className="eyebrow on-dark">Insights</span>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.05] text-frost sm:text-5xl lg:text-6xl">
            Fractional COO insights and operating case studies.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-frost/75">
            See the plans, scorecards and operating changes used inside founder-led businesses across transport, mining, electrical, healthcare and more.
          </p>
        </div>
      </section>

      {/* Feature */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <Link
          to="/insights/$slug"
          params={{ slug: feature.slug }}
          className="group block overflow-hidden rounded-3xl border border-border bg-card transition hover:shadow-elevated"
        >
          <div className="grid gap-0 md:grid-cols-2">
            <div className="gradient-navy relative flex min-h-[280px] items-end p-10 text-frost">
              <div className="grid-lines absolute inset-0 opacity-20" />
              <div className="relative">
                <span className="eyebrow on-dark" aria-hidden="true">{feature.category} · {feature.read} · Updated {formatPostDate(modifiedAt(feature.slug))}</span>
                <h2 className="mt-3 font-serif text-3xl leading-tight text-frost sm:text-4xl">
                  {feature.title}
                </h2>
              </div>
            </div>
            <div className="flex flex-col justify-between p-10">
              <p className="text-base leading-relaxed text-muted-foreground" aria-hidden="true">{feature.dek}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-midnight transition group-hover:gap-3" aria-hidden="true">
                Read the article <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>

        {/* Grid */}
        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to="/insights/$slug"
              params={{ slug: p.slug }}
              className="group flex flex-col overflow-hidden card-panel card-interactive"
            >
              <span className="eyebrow text-steel" aria-hidden="true">{p.category} · {p.read} · Updated {formatPostDate(modifiedAt(p.slug))}</span>
              <h3 className="mt-4 font-serif text-2xl leading-tight text-foreground">{p.title}</h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground" aria-hidden="true">{p.dek}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-midnight transition group-hover:gap-3" aria-hidden="true">
                Read the article <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <NextStep
        heading="Operating Scan for your own business"
        body="Answer 21 plain statements from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("insights_index_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </div>
  );
}
